import { useMemo, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui';
import AddUserDialog from '../components/users/AddUserDialog';
import { UsersTable } from '../components/users/UsersTable';
import { useCreateUser, useDeleteUser, useIsAdmin, useMe, useUpdateUser, useUsers } from '../hooks';
import { useIntl } from '../hooks/useIntl.js';
import type { UpdateUserInput } from '../hooks/useUsers.js';
import { grantsEqual, toGrantPayload } from '../services/user-grants.js';
import type { AppUser } from '../types/user.js';
import type { UserFormValues } from '../validations/user-form.js';

const EMPTY_FORM_VALUES: UserFormValues = {
  username: '',
  password: '',
  isAdmin: false,
  grants: [],
};

function AccessDenied() {
  const intl = useIntl();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {intl.formatMessage({ id: 'users.accessDenied.title' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600">
          {intl.formatMessage({ id: 'users.accessDenied.accessDeniedDesc' })}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Users() {
  const intl = useIntl();
  const { data: me, isLoading: isLoadingMe } = useMe();
  const isAdminUser = useIsAdmin(me);
  const {
    data: users = [],
    isLoading: isLoadingUsers,
    isRefetching,
    error,
    refetch,
  } = useUsers({ enabled: isAdminUser });
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dialogInitialValues = useMemo<UserFormValues>(() => {
    if (selectedUser) {
      return {
        username: selectedUser.username,
        password: '',
        isAdmin: selectedUser.role === 'admin',
        grants: selectedUser.grants.map((grant) => ({ ...grant })),
      };
    }
    return { ...EMPTY_FORM_VALUES, grants: [] };
  }, [selectedUser]);

  const resetDialog = () => {
    setIsDialogOpen(false);
    setDialogMode('create');
    setSelectedUser(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setIsDialogOpen(true);
    } else {
      resetDialog();
    }
  };

  const handleDialogSubmit = async (values: UserFormValues): Promise<void | string> => {
    const desiredRole = values.isAdmin ? 'admin' : 'member';
    try {
      if (dialogMode === 'create') {
        await createUser.mutateAsync({
          username: values.username.trim(),
          password: values.password,
          role: desiredRole,
          ...(values.isAdmin ? {} : { grants: toGrantPayload(values.grants) }),
        });
        resetDialog();
        return;
      }

      if (!selectedUser) {
        return intl.formatMessage({ id: 'users.error.noUserSelected' });
      }

      const trimmedUsername = values.username.trim();
      const payload: UpdateUserInput = { id: selectedUser.id };

      if (trimmedUsername !== selectedUser.username) {
        payload.username = trimmedUsername;
      }
      if (desiredRole !== selectedUser.role) {
        payload.role = desiredRole;
      }
      if (values.password) {
        payload.password = values.password;
      }

      if (!values.isAdmin && !grantsEqual(values.grants, selectedUser.grants)) {
        payload.grants = toGrantPayload(values.grants);
      }

      if (
        payload.username === undefined &&
        payload.role === undefined &&
        payload.password === undefined &&
        payload.grants === undefined
      ) {
        resetDialog();
        return;
      }

      await updateUser.mutateAsync(payload);
      resetDialog();
      return;
    } catch (err) {
      if (err instanceof Error) {
        return err.message;
      }
      return intl.formatMessage({ id: 'users.error.failedToSave' });
    }
  };

  if (isLoadingMe) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{intl.formatMessage({ id: 'users.title' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground">
            {intl.formatMessage({ id: 'users.loadingAccount' })}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!me || !isAdminUser) {
    return <AccessDenied />;
  }

  const isSubmitting = dialogMode === 'create' ? createUser.isPending : updateUser.isPending;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteUserMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError(intl.formatMessage({ id: 'users.failedToDelete' }));
      }
    }
  };

  const isDeleting = deleteUserMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{intl.formatMessage({ id: 'users.title' })}</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoadingUsers || isRefetching}
              >
                {isLoadingUsers || isRefetching
                  ? intl.formatMessage({ id: 'users.refreshing' })
                  : intl.formatMessage({ id: 'users.refresh' })}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setDialogMode('create');
                  setSelectedUser(null);
                  setIsDialogOpen(true);
                }}
              >
                {intl.formatMessage({ id: 'users.addUser' })}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive">
              {intl.formatMessage({ id: 'users.error.failedToLoad' }, { error: error.message })}
            </div>
          ) : isLoadingUsers ? (
            <div className="text-sm text-foreground">
              {intl.formatMessage({ id: 'users.loading' })}
            </div>
          ) : (
            <UsersTable
              users={users}
              currentUserId={me.id}
              onEdit={(user) => {
                setDialogMode('edit');
                setSelectedUser(user);
                setIsDialogOpen(true);
              }}
              onDelete={(user) => {
                setDeleteTarget(user);
                setDeleteError(null);
              }}
            />
          )}
        </CardContent>
      </Card>

      <AddUserDialog
        open={isDialogOpen}
        mode={dialogMode}
        initialValues={dialogInitialValues}
        isSubmitting={isSubmitting}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{intl.formatMessage({ id: 'users.confirmDelete' })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            {intl.formatMessage(
              { id: 'users.confirmDeleteDesc' },
              { username: deleteTarget?.username ?? '' },
            )}
          </p>
          {deleteError && <div className="text-sm text-destructive">{deleteError}</div>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError(null);
              }}
              disabled={isDeleting}
            >
              {intl.formatMessage({ id: 'users.cancel' })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting
                ? intl.formatMessage({ id: 'users.deleting' })
                : intl.formatMessage({ id: 'users.delete' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
